import {AdjacencyMap} from './GraphUtilConstants';

/**
 * Determines if a node in a graph is "dominated" by another node.
 *
 * We say that a node (A) is dominated by another node (B) if all paths from the root of the graph to A include B.
 *
 * https://en.wikipedia.org/wiki/Dominator_(graph_theory)
 *
 * @param targetNodeId The node
 * @param dominatorNodeId The node we expect the target to be dominated by
 * @param rootNodeId The root node of the graph
 * @param adjacencyMap The adjacency map of the graph
 * @returns Whether or not the targetNode is dominated by dominatorNodeId
 */
export default function isNodeDominated(
  targetNodeId: string,
  dominatorNodeId: string,
  rootNodeId: string,
  adjacencyMap: AdjacencyMap
): boolean {
  if (targetNodeId === dominatorNodeId) {
    return true;
  }

  const stack: Array<{nodeId: string; nodeIdsInPath: Set<string>}> = [
    {nodeId: rootNodeId, nodeIdsInPath: new Set()},
  ];
  while (stack.length > 0) {
    const {nodeId, nodeIdsInPath} = stack.pop()!;
    if (nodeId === targetNodeId) {
      if (!nodeIdsInPath.has(dominatorNodeId)) {
        return false;
      }
      continue;
    }
    // Terminates the graph traversal if we've already seen this node in the path.
    // This prevents the traversal from getting stuck in a cycle.
    if (nodeIdsInPath.has(nodeId)) {
      continue;
    }

    const neighbors = adjacencyMap[nodeId];
    if (neighbors == null) {
      continue;
    }
    for (const neighbor of neighbors) {
      stack.push({nodeId: neighbor, nodeIdsInPath: new Set([...nodeIdsInPath, nodeId])});
    }
  }

  return true;
}
